import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Loader2, User, CalendarDays, AlertTriangle } from 'lucide-react';
import { Button } from '../../ui/button';
import { STATUTS_ACTION, ORIGINES_ACTION, Badge, Filtres, compterPar, aujourdhui } from '../constantes';
import { dateFr } from '../shared';
import { ActionDialog } from './ActionDialog';
import { Suggestions } from './Suggestions';

const enRetard = (a) => a.echeance && a.echeance < aujourdhui() && ['a_faire', 'en_cours'].includes(a.statut);

const CarteAction = ({ a, onOpen }) => (
  <button type="button" onClick={() => onOpen(a)} className="w-full rounded-xl border border-slate-200 bg-white p-4 text-left transition-[border-color,box-shadow] hover:border-[#8DC63F] hover:shadow-md" data-testid={`action-${a.action_id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-xs font-semibold text-slate-500">{a.numero}</span>
      <Badge meta={STATUTS_ACTION[a.statut]} testId={`action-${a.action_id}-statut`} />
      <span className="text-xs text-slate-500">{ORIGINES_ACTION[a.origine]}{a.origine_libelle ? ` · ${a.origine_libelle}` : ''}</span>
      {enRetard(a) && <span className="flex items-center gap-1 text-xs font-semibold text-red-600"><AlertTriangle className="h-3.5 w-3.5" /> En retard</span>}
    </div>
    <p className="mt-2 line-clamp-2 text-sm text-slate-500">{a.constat}</p>
    <p className="mt-1 text-sm font-semibold text-[#0F172A]">{a.action}</p>
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
      {a.responsable && <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {a.responsable}</span>}
      {a.echeance && <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {dateFr(a.echeance)}</span>}
      {(a.indicateurs || []).map((n) => <span key={n} className="rounded bg-slate-100 px-1.5 font-semibold text-slate-600">I{n}</span>)}
    </div>
  </button>
);

export const PlanAmelioration = ({ onChanged }) => {
  const [actions, setActions] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [filtre, setFiltre] = useState('');
  const [ouverte, setOuverte] = useState(null);
  const charger = useCallback(() => {
    Promise.all([axios.get('/qualiopi/actions'), axios.get('/qualiopi/actions/suggestions')])
      .then(([a, s]) => { setActions(a.data.items); setSuggestions(s.data); })
      .catch(() => toast.error("Impossible de charger le plan d'amélioration"));
  }, []);
  useEffect(() => { charger(); }, [charger]);
  const apresEnregistrement = () => { setOuverte(null); charger(); onChanged(); };
  const depuisSuggestion = (s) => setOuverte({ origine: s.origine, origine_ref: s.ref, origine_libelle: s.origine_libelle, constat: s.constat, indicateurs: s.indicateurs });

  if (!actions) return <div className="flex justify-center py-12 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…</div>;
  const visibles = actions.filter((a) => !filtre || a.statut === filtre);
  const retard = actions.filter(enRetard).length;
  return (
    <div className="space-y-4" data-testid="plan-amelioration">
      <Suggestions items={suggestions} onCreer={depuisSuggestion} onChanged={charger} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Filtres options={STATUTS_ACTION} valeur={filtre} onChange={setFiltre} compteurs={compterPar(actions, 'statut')} prefixe="actions" />
        <Button onClick={() => setOuverte({})} className="bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid="action-nouvelle"><Plus className="h-4 w-4 mr-1" /> Nouvelle action</Button>
      </div>
      {retard > 0 && <p className="text-sm font-medium text-red-600" data-testid="actions-en-retard">{retard} action{retard > 1 ? 's' : ''} en retard sur {retard > 1 ? 'leur' : 'son'} échéance</p>}
      {visibles.length === 0
        ? <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="actions-vide">Aucune action{filtre ? ' pour ce statut' : " d'amélioration pour l'instant"}.</div>
        : <div className="grid gap-3 lg:grid-cols-2">{visibles.map((a) => <CarteAction key={a.action_id} a={a} onOpen={setOuverte} />)}</div>}
      <ActionDialog action={ouverte} onClose={() => setOuverte(null)} onSaved={apresEnregistrement} />
    </div>
  );
};
